import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import useForm from '@/hooks/useForm';
import { States } from '@/helpers/common/states';
import { useDialog } from '@/hooks/useDialog';
import { useEventCloseCsvPageFilter } from '@/context/FinancialPageContext';
import Event from '@/model/Event';
import EventStatus from '@/model/EventStatus';
import Page from '@/model/Page';
import api, { AxiosError } from '@/services/api';
import axios from 'axios';
import { getBoolean, getItem } from '@/helpers/common/localStorage';
import { REACT_APP_AUTH, REACT_APP_BASE_URL, REACT_APP_SUFFIX_BASE_URL } from '@/utils/config';
import { Auth } from '@/model/Auth';
import { EventCloseCsvContainer } from './ui';
import { FormInput, ShouldShowModal } from '../types';

export const EventCloseCsvWrapper: React.FC = (): JSX.Element => {
  const [state, setState] = useState<States>(States.default);
  const [shouldShowModal, setShouldShowModal] = useState<ShouldShowModal>(ShouldShowModal.filter);
  const [selectedEventId, setSelectedEventId] = useState<string>();
  const [fileName, setFileName] = useState<string>();
  const [selectedFile, setSelectedFile] = useState<File>();

  const { title, visible, onChangeTitle, onToggle } = useDialog();
  const { page, setPage, clearPage } = useEventCloseCsvPageFilter();

  const handleOnPage = async (pageData: Page<Event, Event>): Promise<void> => {
    try {
      setState(States.loading);
      const { data } = await api.post<Page<Event, Event>>('/event/close/financial/page', pageData);
      if (data) {
        setPage(data);
      }
    } catch (error) {
      const err = error as AxiosError;
      if (err.response?.status !== 401) {
        toast.error(err.message);
      }
    } finally {
      setState(States.default);
    }
  };

  const { formData, formErrors, onChangeFormInput, isFormValid, resetForm } = useForm({
    initialData: {
      filterSearch: '',
      inputSearch: '',
    },
    formatters: {},
  });

  const handleOnShouldShowModal = ({
    value,
    newTitleModal,
    eventId,
  }: {
    value: ShouldShowModal;
    newTitleModal: string | React.ReactNode;
    eventId?: string;
  }): void => {
    setShouldShowModal(value);
    onChangeTitle(newTitleModal);
    if (eventId) {
      setSelectedEventId(eventId);
    }
    onToggle();
  };

  const handleOnClearFilter = async (): Promise<void> => {
    resetForm();
    const newPage: Page<Event, Event> = {
      page: 1,
      pageSize: 10,
      entity: {
        eventStatus: EventStatus.ACTIVE,
      } as Event,
      sort: 'startDate',
      order: 'DESC',
      total: 1,
    };
    clearPage();
    await handleOnPage(newPage);
  };

  const handleOnFilter = async (): Promise<void> => {
    if (isFormValid()) {
      const newPage: Page<Event, Event> = {
        page: 1,
        pageSize: 10,
        entity: {
          name: formData[FormInput.inputSearch],
        } as Event,
        sort: 'startDate',
        order: 'DESC',
        total: 1,
      };
      onToggle();

      await handleOnPage(newPage);
    }
  };

  const handleOnChangeFileInput = (file: File | undefined): void => {
    if (file) {
      setFileName(file.name);
      setSelectedFile(file);
    }
  };

  const handleOnLoadCsv = async (): Promise<void> => {
    try {
      setState(States.loading);
      if (selectedEventId && fileName && selectedFile) {
        const baseUrl = String(REACT_APP_BASE_URL);
        const prefixBaseUrl = String(REACT_APP_SUFFIX_BASE_URL);
        const appAuth = String(REACT_APP_AUTH);

        const isAuthenticated = getBoolean(appAuth);
        if (isAuthenticated) {
          const authData: Auth = getItem(appAuth);
          const form = new FormData();
          form.append('file', selectedFile, fileName);
          form.append('fileName', fileName);

          const { data } = await axios.post<Blob>(
            `${baseUrl}${prefixBaseUrl}event-close/${selectedEventId}/csv`,
            form,
            {
              responseType: 'blob',
              headers: {
                accept: '*/*',
                Authorization: `Bearer ${authData.token}`,
                'Content-Type': 'multipart/form-data',
              },
            },
          );
          const href = URL.createObjectURL(data);
          const link = document.createElement('a');
          link.href = href;
          link.setAttribute('download', 'report.csv'); // Or extract from Content-Disposition header
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(href);
          setFileName(undefined);
          setSelectedFile(undefined);
          setSelectedEventId(undefined);
          onToggle();
        }
      }
    } catch (error) {
      const err = error as AxiosError;
      if (err.response?.status !== 401) {
        toast.error(err.message);
      }
    } finally {
      setState(States.default);
    }
  };

  const handleOnPageChange = async (newPageNumber: number): Promise<void> => {
    const newPage: Page<Event, Event> = {
      ...page,
      page: newPageNumber,
    };
    await handleOnPage(newPage);
  };

  useEffect(() => {
    handleOnPage(page);
  }, []);

  return (
    <EventCloseCsvContainer
      state={state}
      title={title}
      visible={visible}
      onToggle={onToggle}
      shouldShowModal={shouldShowModal}
      onShouldShowModal={handleOnShouldShowModal}
      onClearFilter={handleOnClearFilter}
      formData={formData}
      formErrors={formErrors}
      onChangeFormInput={onChangeFormInput}
      onFilter={handleOnFilter}
      fileName={fileName}
      onChangeFileInput={handleOnChangeFileInput}
      loadCsv={handleOnLoadCsv}
      currentPage={page}
      onPageChange={handleOnPageChange}
    />
  );
};
